<template>
  <div id="book" ref="book" class="scrollable" @scroll="handleScroll">
    <div id="bookInner" ref="bookInner">
      <div
        v-for="doc in activeDocs"
        :key="doc.docRef.id"
        :id="`${id}-${doc.docRef.id}`"
        :ref="`doc-${id}-${doc.docRef.id}-${doc.docIndex}`"
      >
        <doc-detail :repoId="id" :id="doc.docRef.id" />
      </div>
    </div>
  </div>
</template>

<style>
#bookInner {
  padding: 8px;
  overflow-x: hidden;
}
</style>

<script>
import DocDetail from "@/components/DocDetail.vue";
import { newRepo } from "@/domain/repo";
import { newBookmark } from "@/domain/bookmark";

export default {
  name: "Book",
  props: {
    id: String
  },
  components: {
    DocDetail
  },
  data() {
    return {
      scrollLoadOffset: 100,
      repo: newRepo(),
      bookmark: newBookmark(),
      activeDocs: [],
      activeIndex: 0,
      reloading: true
    };
  },
  watch: {
    id: {
      immediate: true,
      async handler() {
        this.bookmark = await this.$ioc.bookmarkService.getBookmark(this.id);
        this.repo = await this.$ioc.repoService.getRepo(this.id);

        const bookmarkIndex = (() => {
          let bookmarkIndex = 0;
          if (this.bookmark !== undefined && this.bookmark !== null) {
            bookmarkIndex = this.bookmark.index;
          }
          return bookmarkIndex;
        })();

        this.activeDocs = [bookmarkIndex]
          .filter(
            bookmarkIndex =>
              bookmarkIndex >= 0 && bookmarkIndex < this.repo.docs.length
          )
          .map(bookmarkIndex => ({
            docIndex: bookmarkIndex,
            docRef: this.repo.docs[bookmarkIndex]
          }));
        this.activeIndex = this.activeDocs.findIndex(
          doc => doc.index === bookmarkIndex
        );
      }
    }
  },
  methods: {
    handleScroll: async function(el) {
      const parentDimension = {
        x: this.$refs.book.offsetLeft,
        y: this.$refs.book.offsetTop,
        width: this.$refs.book.offsetWidth,
        height: this.$refs.book.offsetHeight
      };
      const focussedDocIndex = Object.entries(this.$refs)
        .filter(entry => entry[0].startsWith("doc"))
        .filter(entry => {
          if (entry === undefined || entry === null) {
            return false;
          }
          if (entry[1] === undefined || entry[1] === null) {
            return false;
          }
          if (entry[1][0] === undefined || entry[1][0] === null) {
            return false;
          }
          return true;
        })
        .map(entry => {
          return {
            docIndex: parseInt(
              entry[0].substring(entry[0].lastIndexOf("-") + 1)
            ),
            x: entry[1][0].offsetLeft,
            y: entry[1][0].offsetTop,
            width: entry[1][0].offsetWidth,
            height: entry[1][0].offsetHeight
          };
        })
        .map(childDimension => {
          const parentStart = el.srcElement.scrollTop;
          const parentHeight = parentDimension.height;
          const parentEnd = parentStart + parentHeight;

          const childStart = childDimension.y;
          const childHeight = childDimension.height;
          const childEnd = childStart + childHeight;

          const maxStart = Math.max(parentStart, childStart);
          const minEnd = Math.min(parentEnd, childEnd);

          const diff = Math.max(0, minEnd - maxStart);

          const percentage = diff / parentHeight;

          return {
            docIndex: childDimension.docIndex,
            percentage
          };
        })
        .reduce((max, curr) => (curr.percentage > max.percentage ? curr : max))
        .docIndex;

      const focussedDocRef = this.repo.docs[focussedDocIndex];
      const focussedIndex = this.activeDocs.findIndex(
        doc => doc.docIndex === focussedDocIndex
      );

      if (focussedIndex === 0 && focussedDocIndex > 0) {
        this.activeDocs = [
          {
            docIndex: focussedDocIndex - 1,
            docRef: this.repo.docs[focussedDocIndex - 1]
          },
          ...this.activeDocs
        ];
      }
      if (
        focussedIndex === this.activeDocs.length - 1 &&
        focussedDocIndex < this.repo.docs.length - 1
      ) {
        this.activeDocs = [
          ...this.activeDocs,
          {
            docIndex: focussedDocIndex + 1,
            docRef: this.repo.docs[focussedDocIndex + 1]
          }
        ];
      }

      await this.$ioc.bookmarkService.setIndex(
        this.id,
        this.repo,
        focussedDocRef,
        focussedIndex
      );
    }
  }
};
</script>
